import type { Theme } from '@mui/material';
import {
  Card as MuiCard,
  CardContent,
  Typography,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/styles';
import { useHistory } from 'react-router-dom';
import noop from 'lodash/noop';
import { lighten } from '@mui/system';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import type { ProductFamily, ServiceStatusInfo } from '../types';
import { useMeQuery } from '../../../components/hooks/useMeQuery';
import Pill from './Pill';
import getRibbonVariables from '../utils/getRibbonVariables';
import { ServiceStatus } from '../types';
import { useCheckAnyPolicyHasStatus } from '../../../components/hooks/queries/policies/useCheckAnyPolicyHasStatus';
import { shouldDisplayRibbon } from '../utils/shouldDisplayRibbon';
import {
  EXPIRED_STATUS,
  RENEWED_STATUS,
} from '../../../console/_statics/policy.statics';

import { useLeadConvertor } from '../hooks/useLeadConvertor';
import {
  getServiceStatusColor,
  getServiceStatusText,
} from '../utils/serviceStatus';
import { FEATURE_FLAG_KEYS } from '../../../config/growthbook';
import { isNonSelfServeProductFamilyCode } from '../utils/productCodes';

interface ServiceCardProps {
  productFamily: ProductFamily;
  serviceStatusInfos: ServiceStatusInfo[];
  isPoolTenant?: boolean;
}

export const ServiceCard = ({
  productFamily,
  serviceStatusInfos,
  isPoolTenant = false,
}: ServiceCardProps) => {
  const history = useHistory();
  const theme = useTheme();
  const { data: me } = useMeQuery();
  const accountId = me?.account?.id;
  const ribbonVariables = getRibbonVariables(theme, productFamily.ribbon);
  const crsEnableSelfServe = useFeatureIsOn(
    FEATURE_FLAG_KEYS.CRS_ENABLE_SELF_SERVE
  );

  const serviceStatusInfo = serviceStatusInfos.find(
    (_serviceStatusInfo) =>
      _serviceStatusInfo.productFamily.id === productFamily.id
  );
  const serviceStatus = serviceStatusInfo?.status ?? ServiceStatus.NotActivated;

  const hasExpiredPolicy = useCheckAnyPolicyHasStatus(EXPIRED_STATUS);
  const hasRenewedPolicy = useCheckAnyPolicyHasStatus(RENEWED_STATUS);
  const leadConvertor = useLeadConvertor(isPoolTenant);

  // For Pool tenants, we don't use lead conversion, so we don't need to check loading state
  const isCardDisabled = isPoolTenant
    ? productFamily.upcomingService
    : productFamily.upcomingService || leadConvertor.isLoading;

  const handleServiceCardClick = () => {
    try {
      // Determine the correct path based on tenant
      const basePath = isPoolTenant
        ? '/pool/resilience/catalog'
        : '/customer/resilience/catalog';
      const serviceIntroPath = `${basePath}/services/${productFamily.id}/intro`;

      if (serviceStatus !== ServiceStatus.NotActivated) {
        history.push(serviceIntroPath);
        return;
      }

      // Only perform lead conversion for Customer tenants
      if (!isPoolTenant) {
        leadConvertor.mutate({ productFamily }); // fire-and-forget
      }

      history.push(serviceIntroPath);
    } catch (error) {
      const userInfo = {
        accountId,
        name: `{${me?.firstName} ${me?.lastName}}`,
        email: me?.email,
        error,
      };

      console.error('Error on handling service card click', userInfo);
    }
  };

  // Use tenant-aware path check
  const currentServicesPath = isPoolTenant
    ? '/pool/resilience/services'
    : '/customer/resilience/services';

  if (
    history.location.pathname === currentServicesPath &&
    serviceStatus !== ServiceStatus.NotActivated
  ) {
    return null;
  }

  const isRibbonVisible = shouldDisplayRibbon({
    uniqueCode: productFamily.code,
    hasExpiredPolicy,
    hasRenewedPolicy,
    ribbon: productFamily.ribbon,
    serviceStatus,
  });

  const shouldDisplayPill = () => {
    if (
      isNonSelfServeProductFamilyCode(productFamily.code) &&
      serviceStatus === ServiceStatus.RequestReceived
    ) {
      return true;
    }

    if (crsEnableSelfServe && serviceStatus === ServiceStatus.RequestReceived) {
      return false;
    }

    if (serviceStatus !== ServiceStatus.NotActivated) {
      return true;
    }

    return false;
  };

  const displayPill = shouldDisplayPill();

  return (
    <div style={{ display: 'grid', position: 'relative' }}>
      {isRibbonVisible && (
        <StyledRibbon
          bgColor={ribbonVariables.bgColor}
          textColor={ribbonVariables.textColor}
        >
          {productFamily.ribbon}
        </StyledRibbon>
      )}
      <Card
        elevation={1}
        sx={{
          position: 'relative',
          borderLeft: `8px solid ${theme.palette.primary.cowbellBlue}`,
          cursor: isCardDisabled ? 'not-allowed' : 'pointer',
          opacity: productFamily.upcomingService ? 0.5 : 1,

          '&:hover': {
            boxShadow: productFamily.upcomingService
              ? 'none'
              : `0 4px 20px ${theme.palette.common.lightestBlack15}`,
            transform: productFamily.upcomingService ? 'none' : 'scale(1.02)',
            transition: productFamily.upcomingService
              ? 'none'
              : 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
            outline: productFamily.upcomingService
              ? 'none'
              : `3px solid ${theme.palette.primary.cowbellBlue}`,
          },
        }}
        onClick={isCardDisabled ? noop : handleServiceCardClick}
        data-testid={`service-card-${productFamily.id}`}
      >
        {displayPill &&
          Object.values(ServiceStatus).includes(serviceStatus) && (
            <div style={{ position: 'absolute', top: '1rem', right: '1rem' }}>
              <Pill
                text={getServiceStatusText(serviceStatus)}
                circleColor={getServiceStatusColor(serviceStatus, theme)}
                textColor={getServiceStatusColor(serviceStatus, theme)}
                backgroundColor={lighten(
                  getServiceStatusColor(serviceStatus, theme),
                  0.9
                )}
              />
            </div>
          )}

        {!isPoolTenant && leadConvertor.isLoading && (
          <LoadingOverlay>
            <CircularProgress />
          </LoadingOverlay>
        )}
        <CardContent
          sx={{
            padding: '0px !important',
            marginTop: '1rem',
          }}
        >
          <Typography
            gutterBottom
            variant="body1"
            noWrap
            sx={{
              fontWeight: 700,
              textWrap: 'wrap',
            }}
          >
            {productFamily.name}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: '1rem',
              fontWeight: '400',
              lineHeight: '1.33334rem',
            }}
          >
            {productFamily.shortDescription ?? ''}
          </Typography>
        </CardContent>
      </Card>
    </div>
  );
};

const Card = styled(MuiCard)({
  padding: '1.5rem',
  position: 'relative',
});

const StyledRibbon = styled('div')(
  ({
    bgColor,
    textColor,
  }: {
    bgColor: string;
    textColor: string;
    theme: Theme;
  }) => ({
    '--f': '.5em' /* control the folded part*/,
    '--r': '.8em' /* control the ribbon shape */,

    position: 'absolute',
    top: '-10px',
    right: 'calc(-1*var(--f))',
    'padding-inline': '1em',
    'line-height': 1.8,
    background: bgColor,
    'border-bottom': `var(--f) solid #0005`,
    'border-left': `var(--r) solid #0000`,
    'clip-path': `polygon(0 0,100% 0,100% calc(100% - var(--f)),calc(100% - var(--f)) 100%,
      calc(100% - var(--f)) calc(100% - var(--f)),0 calc(100% - var(--f)),
      var(--r) calc(50% - var(--f)/2))`,
    zIndex: 1,
    'font-size': '1.25rem',
    color: textColor,
    'font-weight': 'bold',
  })
);

const LoadingOverlay = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.common.lightestGray
      : theme.palette.common.softWhite50,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 2,
}));
